import { useQuery } from '@tanstack/react-query'
import { apiFetch } from './client'
import { useAdminState, type AdminIdentity } from '../context/AdminContext'

export interface PlatformAdmin {
  user_id: string
  email: string | null
  role: string | null
  active: boolean | null
  created_at: string | null
  last_sign_in_at: string | null
}
export interface AdminsResponse {
  admins: PlatformAdmin[]
  total: number
}

export const ADMINS_QUERY_KEY = ['admin-admins'] as const

// AdminsView's own list fetch. Not part of the super-admin gating path.
export function useAdminsQuery() {
  return useQuery<AdminsResponse>({
    queryKey: ADMINS_QUERY_KEY,
    queryFn: () => apiFetch<AdminsResponse>('/admin/admins'),
  })
}

/** True only for an ACTIVE super_admin per whoami's platform_role / platform_admin_active. */
export function isSuperAdminIdentity(identity: AdminIdentity | null | undefined): boolean {
  return identity?.platform_role === 'super_admin' && identity.platform_admin_active === true
}

/**
 * Whether the signed-in admin is an ACTIVE super_admin, read from the bootstrap whoami
 * (`platform_role` + `platform_admin_active`; `role` is the workspace role). No extra
 * request: whoami has already resolved before any screen renders.
 *
 * UI convenience only: it hides write controls. The server re-checks the tier on every
 * super-admin write (403 X-Required-Tier: super_admin), and that refusal is shown inline.
 */
export function useIsSuperAdmin(): { isSuperAdmin: boolean; isLoading: boolean } {
  const { identity } = useAdminState()
  return { isSuperAdmin: isSuperAdminIdentity(identity), isLoading: false }
}
